import React from 'react';
import { gql } from '@soundxyz/gql-string';
import {
  hasReadNotification,
  NotificationInfo,
  NotificationWrapper,
} from '@components/Notifications/shared';
import { useUserNotificationStatus } from '@components/Notifications/useUserNotificationStatus';
import { FollowButton } from '@components/basics/FollowButton';
import {
  type FragmentType,
  getFragment,
  RepostNotificationInfoFragmentDoc,
} from '@graphql/reactQuery';
import { PostSingleUser } from './PostSingleUser';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment RepostNotificationInfo on NewRepost {
    id
    post {
      id
      webappUri

      ...NotificationPostContent
    }
    reposter {
      id
      artist {
        id
      }
      ...PostSingleUser
    }
    timestamp
  }
`);

export function RepostNotificationCard({
  notificationFrag,
}: {
  notificationFrag: FragmentType<typeof RepostNotificationInfoFragmentDoc>;
}) {
  const { reposter, post, timestamp } = getFragment(
    RepostNotificationInfoFragmentDoc,
    notificationFrag,
  );

  const { previousTimestamp } = useUserNotificationStatus();

  if (!post || !reposter) return null;

  const hasRead = previousTimestamp ? hasReadNotification({ timestamp, previousTimestamp }) : false;

  return (
    <NotificationWrapper>
      <PostSingleUser
        postWebappUri={post.webappUri}
        postContentFrag={post}
        userFrag={reposter}
        action="reposted"
      />

      <div className="z-above1 flex min-w-[80px] max-w-[80px] flex-shrink-0 items-center justify-end gap-1 sm:gap-1.5 dt:min-w-[136px] dt:gap-4">
        <div className="block lt:hidden">
          <FollowButton
            userId={reposter.id}
            sourceLocation="Repost Notification"
            variant="notification"
            size="XS"
            useIcon
            artistId={reposter.artist?.id}
          />
        </div>
        <div className="hidden lt:block">
          <FollowButton
            userId={reposter.id}
            sourceLocation="Repost Notification"
            variant="notification"
            size="XS"
            artistId={reposter.artist?.id}
          />
        </div>
        <NotificationInfo timeStamp={timestamp} hasRead={hasRead} />
      </div>
    </NotificationWrapper>
  );
}
